import { createApiClient } from "../../../api/apiClient";
import { AppDispatch } from "../../../store";
import { setLeadershipWalks } from "../../../store/slices/leaderShipWalkSlice";
import {
  handleLeadershipWalkErrorWithFriendlyMessage,
  LEADERSHIP_WALK_API_NAMES,
} from "../../../store/utils/leadershipWalkErrorHandler";

interface FetchWalksParams {
  location: string;
  zone?: string;
  frequency?: string;
  page?: number;
  pageSize?: number;
  dispatch: AppDispatch;
  createdBy?: string;
  modifiedBy?: string;
  finalizedBy?: string; // Accept finalizedBy for compatibility with screens
  action?: string;
  period?: string;
  week?: string;
  year?: string;
}
export const fetchLeadershipWalks = async ({
  location,
  zone,
  frequency,
  page = 1,
  pageSize = 200,
  dispatch,
  createdBy,
  modifiedBy,
  finalizedBy,
  action,
  period,
  week,
  year,
}: FetchWalksParams): Promise<any> => {
  try {
    const api = await createApiClient();
    let endpoint = "/leadership-walks?";

    // Add required parameters
    endpoint += `location=${encodeURIComponent(location)}`;
    endpoint += `&walkType=${encodeURIComponent("Leadership Walk")}`;
    endpoint += `&hiddenWalk=${encodeURIComponent("Leadership Walk")}`;
    endpoint += `&page=${page}`;
    endpoint += `&pageSize=${pageSize}`;

    // Add optional parameters
    if (zone) {
      endpoint += `&zone=${encodeURIComponent(zone)}`;
    }
    if (frequency) {
      endpoint += `&frequency=${encodeURIComponent(frequency)}`;
    }
    if (createdBy) {
      endpoint += `&createdBy=${encodeURIComponent(createdBy)}`;
    }
    // Use finalizedBy as the query param if provided, otherwise fallback to modifiedBy
    if (finalizedBy) {
      endpoint += `&finalizedBy=${encodeURIComponent(finalizedBy)}`;
    } else if (modifiedBy) {
      endpoint += `&modifiedBy=${encodeURIComponent(modifiedBy)}`;
    }
    if (action) {
      endpoint += `&status=${encodeURIComponent(action)}`;
    }
    if (period) {
      endpoint += `&period=${encodeURIComponent(period)}`;
    }
    if (week) {
      endpoint += `&week=${encodeURIComponent(week)}`;
    }
    if (year) {
      endpoint += `&year=${encodeURIComponent(year)}`;
    }

    // Make the API request
    const response = await api.get(endpoint);

    // Process the response
    if (response.status === 200 && response.data) {
      // Check if data is in the expected format
      const walks = response.data.data || [];

      // Validate walks data
      if (!walks || !Array.isArray(walks)) {
        const noDataError = new Error("Invalid response format: expected walks array");
        
        handleLeadershipWalkErrorWithFriendlyMessage(
          LEADERSHIP_WALK_API_NAMES.FETCH_WALKS,
          noDataError,
          true,
          { location, zone, frequency, reason: "Invalid response structure" }
        );
        
        return {
          success: false,
          error: "Invalid data format received from server",
        };
      }

      // Transform data if needed and update Redux store
      dispatch(setLeadershipWalks(walks));

      // Return the data for further processing if needed
      return {
        success: true,
        data: walks,
        pagination: response.data.pagination || {
          totalCount: walks.length,
          totalPages: 1,
          currentPage: page,
          pageSize: pageSize,
        },
      };
    } else {
      console.error(
        "Invalid response format from leadership-walks endpoint:",
        response
      );
      
      const invalidResponseError = new Error("Invalid response format");
      handleLeadershipWalkErrorWithFriendlyMessage(
        LEADERSHIP_WALK_API_NAMES.FETCH_WALKS,
        invalidResponseError,
        true,
        { location, zone, frequency }
      );
      
      return {
        success: false,
        error: "Invalid data format received from server",
      };
    }
  } catch (error: any) {
    console.error("Error fetching leadership walks:", error);

    // Use centralized error handler with modal overlay
    handleLeadershipWalkErrorWithFriendlyMessage(
      LEADERSHIP_WALK_API_NAMES.FETCH_WALKS,
      error,
      true,
      { location, zone, frequency }
    );

    // Return a structured error response
    return {
      success: false,
      error: error.message || "Failed to fetch leadership walks",
      details: error.response?.data || {},
    };
  }
};
